import { Grid } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import FieldContainer from '../../../FormFields/FieldContainer';
import FormButtonOptions from '../../../FormFields/FormButtonOptions';
import FormDataDivider from '../../../FormFields/FormDataDivider';
import FormMoneyField from '../../../FormFields/FormMoneyField';
import FormTextField from '../../../FormFields/FormTextField';
import FormNumberField from '../../../FormFields/FormNumberField';
import FormTitle from '../../../FormFields/FormTitle';
import FormVendorField from '../../../FormFields/FormVendorField';
import { FILE_TYPES } from '../../../formFileTypesEnum';
import { ENVIRONMENTAL } from '../../../formQuestionsEnum';
import { OUTSOURCED_COMPANY_CATEGORIES } from '../../../optionEnums';

const IN_HOUSE_FIELDS = Object.freeze({
  HOUSEKEEPING_SOAP: 'What soap?',
  HOUSEKEEPING_SANITIZER: 'What sanitizer?',
  HOUSEKEEPING_LINERS: 'What liners?',
  HOUSEKEEPING_PAPER_TOWELS: 'What paper towels?',
  HOUSEKEEPING_TOILET_PAPER: 'What toilet paper?',
  HOUSEKEEPING_CHEMICALS: 'What housekeeping chemicals?',
});

HousekeepingSection.propTypes = {
  permissions: PropTypes.object,
};

export default function HousekeepingSection({ permissions }) {
  const {
    data: { outsourcedCompanyCategoriesList },
  } = useSelector((state) => state.transitions);
  const { watch } = useFormContext();
  const isOutsourced = watch(ENVIRONMENTAL.HOUSEKEEPING_IS_OUTSOURCED.fieldName);
  const [focusedField, setFocusedField] = useState(null);

  return (
    [
      ENVIRONMENTAL.HOUSEKEEPING_IS_OUTSOURCED.id,
      ENVIRONMENTAL.HOUSEKEEPING_OUTSOURCED_CATEGORY_ID.id,
      ENVIRONMENTAL.HOUSEKEEPING_OUTSOURCED_HOURS_PER_WEEK.id,
      ENVIRONMENTAL.HOUSEKEEPING_AVERAGE_MONTHLY_COST.id,
      ENVIRONMENTAL.HOUSEKEEPING_AND_LAUNDRY_OUTSOURCE_VENDOR.id,
      ENVIRONMENTAL.HOUSEKEEPING_SOAP.id,
      ENVIRONMENTAL.HOUSEKEEPING_SANITIZER.id,
      ENVIRONMENTAL.HOUSEKEEPING_LINERS.id,
      ENVIRONMENTAL.HOUSEKEEPING_PAPER_TOWELS.id,
      ENVIRONMENTAL.HOUSEKEEPING_TOILET_PAPER.id,
      ENVIRONMENTAL.HOUSEKEEPING_CHEMICALS.id,
    ].some((id) => id in permissions) && (
      <>
        <Grid item xs={12}>
          <FormTitle fieldAttributes={{ questionTitle: 'Housekeeping', fieldType: 'title' }} />
        </Grid>
        {ENVIRONMENTAL.HOUSEKEEPING_IS_OUTSOURCED.id in permissions && (
          <Grid item xs={12}>
            <FieldContainer>
              <FormTitle fieldAttributes={{ questionTitle: 'Department type' }} />
              <FormButtonOptions
                fieldName={ENVIRONMENTAL.HOUSEKEEPING_IS_OUTSOURCED.fieldName}
                fieldAttributes={{
                  isEditable: permissions[ENVIRONMENTAL.HOUSEKEEPING_IS_OUTSOURCED.id],
                  options: [
                    { label: 'Outsourced', value: true },
                    { label: 'In House', value: false },
                  ],
                }}
              />
            </FieldContainer>
          </Grid>
        )}

        {isOutsourced === true && (
          <>
            {ENVIRONMENTAL.HOUSEKEEPING_OUTSOURCED_CATEGORY_ID.id in permissions && (
              <Grid item xs={12}>
                <FieldContainer>
                  <FormTitle fieldAttributes={{ questionTitle: 'Choose Category' }} />
                  <FormButtonOptions
                    fieldName={ENVIRONMENTAL.HOUSEKEEPING_OUTSOURCED_CATEGORY_ID.fieldName}
                    fieldAttributes={{
                      isEditable: permissions[ENVIRONMENTAL.HOUSEKEEPING_OUTSOURCED_CATEGORY_ID.id],
                      options: outsourcedCompanyCategoriesList.filter(
                        (x) =>
                          x.value === OUTSOURCED_COMPANY_CATEGORIES.FULL_DEPARTMENT ||
                          x.value === OUTSOURCED_COMPANY_CATEGORIES.MANAGER_ONLY
                      ),
                    }}
                  />
                </FieldContainer>
              </Grid>
            )}

            {ENVIRONMENTAL.HOUSEKEEPING_OUTSOURCED_HOURS_PER_WEEK.id in permissions && (
              <Grid item xs={12} md={6}>
                <FieldContainer>
                  <FormTitle fieldAttributes={{ questionTitle: 'Hours per week' }} />
                  <FormNumberField
                    fieldName={ENVIRONMENTAL.HOUSEKEEPING_OUTSOURCED_HOURS_PER_WEEK.fieldName}
                    fieldAttributes={{
                      isEditable: permissions[ENVIRONMENTAL.HOUSEKEEPING_OUTSOURCED_HOURS_PER_WEEK.id],
                    }}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                    allowDecimal
                  />
                </FieldContainer>
              </Grid>
            )}
            {ENVIRONMENTAL.HOUSEKEEPING_AVERAGE_MONTHLY_COST.id in permissions && (
              <Grid item xs={12} md={6}>
                <FieldContainer>
                  <FormTitle fieldAttributes={{ questionTitle: 'Average monthly cost' }} />
                  <FormMoneyField
                    fieldName={ENVIRONMENTAL.HOUSEKEEPING_AVERAGE_MONTHLY_COST.fieldName}
                    fieldAttributes={{
                      isEditable: permissions[ENVIRONMENTAL.HOUSEKEEPING_AVERAGE_MONTHLY_COST.id],
                    }}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />
                </FieldContainer>
              </Grid>
            )}
            {ENVIRONMENTAL.HOUSEKEEPING_AND_LAUNDRY_OUTSOURCE_VENDOR.id in permissions && (
              <Grid item xs={12}>
                <FieldContainer>
                  <FormTitle
                    fieldAttributes={{
                      questionTitle: 'Housekeeping and laundry outsourced company',
                      fieldType: 'title',
                    }}
                  />
                  <FormVendorField
                    fieldName={ENVIRONMENTAL.HOUSEKEEPING_AND_LAUNDRY_OUTSOURCE_VENDOR.fieldName}
                    fieldAttributes={{
                      isEditable: permissions[ENVIRONMENTAL.HOUSEKEEPING_AND_LAUNDRY_OUTSOURCE_VENDOR.id],
                    }}
                    invoiceFileTypeId={FILE_TYPES.HOUSEKEEPING_AND_LAUNDRY_OUTSOURCED_COMPANY_INVOICE}
                    contractFileTypeId={FILE_TYPES.HOUSEKEEPING_AND_LAUNDRY_OUTSOURCED_COMPANY_CONTRACT}
                  />
                </FieldContainer>
              </Grid>
            )}
          </>
        )}
        {isOutsourced === false && (
          <>
            {Object.entries(IN_HOUSE_FIELDS).map(([questionKey, questionTitle]) => {
              const question = ENVIRONMENTAL[questionKey];

              if (!question || !(question.id in permissions)) {
                return null;
              }

              return (
                <Grid item xs={12} md={6} key={questionKey}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle }} />
                    <FormTextField
                      fieldName={question.fieldName}
                      fieldAttributes={{
                        isEditable: permissions[question.id],
                      }}
                      focusedField={focusedField}
                      setFocusedField={setFocusedField}
                    />
                  </FieldContainer>
                </Grid>
              );
            })}
          </>
        )}
        <FormDataDivider />
      </>
    )
  );
}
